import type { ClientStore } from '../state/clientStore.js';
import { attachEdgeFade } from '../ui/edgeFade.js';
import { VITALS, isCluster } from './vitals.js';
import type { VitalGroup } from './vitals.js';
import type { Focus } from './focus.js';
import { Visibility } from '../hud/visibility.js';
import { attachMobileSheet } from './mobileSheet.js';
import {
  GROUP_LABELS,
  GROUP_ORDER,
  animateChipChange,
  attachTipLayer,
  buildChip,
  buildClusterUnit,
  formatLiveAge,
  warnTip,
} from './panelElements.js';

export function createVitalsPanel(opts: {
  el: HTMLElement;
  store: ClientStore;
  nowMs: () => number;
  focus: Focus;
  visibility?: Visibility;
  isVisibilityAvailable?: (id: string) => boolean;
}): { controlsSlot: HTMLElement; setSheetExpanded: (expanded: boolean) => void; destroy: () => void } {
  const { el, store, nowMs, focus } = opts;
  const visibility = opts.visibility ?? new Visibility([]);
  const isVisibilityAvailable = opts.isVisibilityAvailable ?? (() => true);

  // Build the static structure once — groups don't change between renders.
  // Only chip content and the LIVE age update.

  const sheetHeader = document.createElement('button');
  sheetHeader.type = 'button';
  sheetHeader.className = 'vitals-sheet-header';
  sheetHeader.setAttribute('aria-expanded', 'false');
  sheetHeader.setAttribute('aria-controls', 'vitals-scroll');
  sheetHeader.setAttribute('aria-label', 'Expand city vitals');

  const sheetHandle = document.createElement('span');
  sheetHandle.className = 'vitals-sheet-handle';
  sheetHandle.setAttribute('aria-hidden', 'true');
  sheetHeader.appendChild(sheetHandle);

  const headlinesEl = document.createElement('span');
  headlinesEl.className = 'vitals-headlines';
  const headlineEls = new Map<string, HTMLElement>();
  const headlineDefs = [['subway', 'Trains'], ['buses', 'Buses'], ['weather', 'Weather']] as const;
  for (const [id, label] of headlineDefs) {
    const item = document.createElement('span');
    item.className = 'vitals-headline';
    item.dataset.headline = id;
    const value = document.createElement('strong');
    value.textContent = '—';
    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    item.append(value, labelEl);
    headlinesEl.appendChild(item);
    headlineEls.set(id, value);
  }
  sheetHeader.appendChild(headlinesEl);
  el.appendChild(sheetHeader);

  const scrollEl = document.createElement('div');
  scrollEl.id = 'vitals-scroll';
  scrollEl.className = 'vitals-scroll';
  el.appendChild(scrollEl);

  const applySheetExpanded = (expanded: boolean): void => {
    el.classList.toggle('vitals--expanded', expanded);
    sheetHeader.setAttribute('aria-expanded', String(expanded));
    sheetHeader.setAttribute('aria-label', expanded ? 'Collapse city vitals' : 'Expand city vitals');
  };

  const measureSheetDetents = (): { compact: number; expanded: number } => {
    const wasExpanded = el.classList.contains('vitals--expanded');
    const liveHeight = el.style.getPropertyValue('--vitals-sheet-height');
    el.style.removeProperty('--vitals-sheet-height');
    el.classList.remove('vitals--dragging', 'vitals--settling');
    el.classList.remove('vitals--expanded');
    const compact = el.getBoundingClientRect().height;
    el.classList.add('vitals--expanded');
    const expanded = el.getBoundingClientRect().height;
    el.classList.toggle('vitals--expanded', wasExpanded);
    if (liveHeight) el.style.setProperty('--vitals-sheet-height', liveHeight);
    return { compact, expanded };
  };
  const sheetController = attachMobileSheet({
    root: el,
    header: sheetHeader,
    getExpanded: () => el.classList.contains('vitals--expanded'),
    setExpanded: applySheetExpanded,
    measureDetents: measureSheetDetents,
  });
  const setSheetExpanded = (expanded: boolean): void => {
    sheetController.setExpanded(expanded);
  };
  const onSheetClick = (): void => {
    setSheetExpanded(!el.classList.contains('vitals--expanded'));
  };
  sheetHeader.addEventListener('click', onSheetClick);

  // LIVE indicator
  const liveEl = document.createElement('div');
  liveEl.className = 'vital-live';

  const dotEl = document.createElement('span');
  dotEl.className = 'vital-live-dot';
  liveEl.appendChild(dotEl);

  const liveTextEl = document.createElement('span');
  liveTextEl.className = 'vital-live-text';

  const liveBoldEl = document.createElement('b');
  liveBoldEl.textContent = 'LIVE';
  liveTextEl.appendChild(liveBoldEl);

  const liveAgeEl = document.createElement('span');
  liveAgeEl.textContent = 'connecting…';
  liveTextEl.appendChild(liveAgeEl);

  /** Most-recent generatedAt across all live (non-down) vitals, or 0 if none. */
  const mostRecentUpdate = (): number => {
    let mostRecent = 0;
    for (const vital of VITALS) {
      const layerIds = isCluster(vital) ? vital.layerIds : [vital.layerId];
      for (const layerId of layerIds) {
        const state = store.get(layerId);
        if (state && state.status !== 'down' && state.generatedAt > mostRecent) {
          mostRecent = state.generatedAt;
        }
      }
    }
    return mostRecent;
  };

  /** Refresh just the LIVE age text from the freshest feed. */
  const updateLiveAge = (): void => {
    const mostRecent = mostRecentUpdate();
    liveAgeEl.textContent = mostRecent > 0 ? formatLiveAge(nowMs() - mostRecent) : 'connecting…';
  };

  liveEl.appendChild(liveTextEl);
  scrollEl.appendChild(liveEl);

  // Group containers — built once
  const groupEls: Record<VitalGroup, HTMLElement> = {
    transit: document.createElement('div'),
    weather: document.createElement('div'),
    city: document.createElement('div'),
  };
  for (const group of GROUP_ORDER) {
    const groupEl = groupEls[group];
    groupEl.className = 'vital-group';
    groupEl.dataset.group = group;

    // Section header (e.g. "Transit") — shown on the desktop panel, hidden in the mobile strip.
    const labelEl = document.createElement('div');
    labelEl.className = 'vital-group-label';
    labelEl.textContent = GROUP_LABELS[group];
    groupEl.appendChild(labelEl);

    scrollEl.appendChild(groupEl);
  }

  // Track chip elements by vital id for targeted updates
  const chipEls = new Map<string, HTMLElement>();

  // Which clusters are expanded. Transient like Focus: defaults collapsed,
  // survives re-renders, resets on reload.
  const expandedClusters = new Set<string>();

  const { hideTip, destroy: destroyTipLayer } = attachTipLayer(el);

  /** Render/update all chips and the LIVE age. */
  const render = (): void => {
    updateLiveAge();
    // Chips are about to be rebuilt — a visible tooltip would point at a
    // removed element, so hide it; the next hover re-shows it instantly.
    hideTip();
    const focusedLayerId = focus.get();

    for (const id of ['subway', 'buses', 'weather']) {
      const vital = VITALS.find((entry) => !isCluster(entry) && entry.id === id);
      const valueEl = headlineEls.get(id);
      if (vital && !isCluster(vital) && valueEl) {
        valueEl.textContent = vital.select(store.get(vital.layerId)).value;
      }
    }

    // Toggle board-level focused class so CSS can dim non-selected chips
    if (focusedLayerId !== null) {
      el.classList.add('vitals--focused');
    } else {
      el.classList.remove('vitals--focused');
    }

    // Update chips — replace if already present, append if new
    for (const vital of VITALS) {
      let newChip: HTMLElement;
      if (isCluster(vital)) {
        const states = vital.layerIds.map((id) => store.get(id));
        const view = vital.select(states);
        const childViews = vital.children.map((c) => c.select(store.get(c.layerId)));
        // Warn only for children whose feed has a real state and is degraded —
        // a child that hasn't connected yet (undefined) is not an outage.
        const childWarns = childViews.map(
          (v, i) => states[i] !== undefined && (v.down || v.stale),
        );
        // The collapsed cluster chip carries the union of its children's
        // warnings (the cluster's own `down` is all-down; one railroad's
        // outage must still surface while collapsed).
        const degradedTips = childViews
          .filter((v, i) => childWarns[i])
          .map((v) => warnTip(v));
        const clusterTip = degradedTips.length > 0 ? degradedTips.join(' · ') : null;
        newChip = buildClusterUnit(
          vital,
          view,
          childViews,
          childWarns,
          clusterTip,
          expandedClusters.has(vital.id),
          focusedLayerId,
          visibility,
          isVisibilityAvailable,
        );
      } else {
        const state = store.get(vital.layerId);
        const view = vital.select(state);
        const warn = state !== undefined && (view.down || view.stale);
        newChip = buildChip(vital, view, focusedLayerId, warn, visibility, isVisibilityAvailable);
      }

      const existing = chipEls.get(vital.id);
      const groupEl = groupEls[vital.group];

      if (existing && groupEl.contains(existing)) {
        animateChipChange(existing, newChip);

        const existingChildren = new Map(
          [...existing.querySelectorAll<HTMLElement>('[data-vital-id]')]
            .map((chip) => [chip.dataset.vitalId, chip] as const),
        );
        for (const nextChild of newChip.querySelectorAll<HTMLElement>('[data-vital-id]')) {
          const previousChild = existingChildren.get(nextChild.dataset.vitalId);
          if (previousChild) animateChipChange(previousChild, nextChild);
        }
        groupEl.replaceChild(newChip, existing);
      } else {
        groupEl.appendChild(newChip);
      }
      chipEls.set(vital.id, newChip);
    }
  };

  const offStore = store.onChange(render) ?? (() => {});
  const offVisibility = visibility.onChange(() => {
    const focusedLayerId = focus.get();
    if (focusedLayerId !== null && !visibility.isVisible(focusedLayerId)) focus.clear();
    render();
  });
  render();

  // Update the LIVE age text every second without rebuilding the whole board.
  const ageTimer = setInterval(updateLiveAge, 1000);

  // Update selection highlight when focus changes without a full rebuild.
  // Keeps aria-pressed and .selected/.vitals--focused in sync cheaply.
  const offFocus = focus.onChange(() => {
    const focusedLayerId = focus.get();

    if (focusedLayerId !== null) {
      el.classList.add('vitals--focused');
    } else {
      el.classList.remove('vitals--focused');
    }

    // Query the DOM rather than walking VITALS so cluster children (which live
    // inside their cluster wrapper, not in chipEls) stay in sync too.
    for (const chip of el.querySelectorAll<HTMLElement>('[data-vital-layer]')) {
      const isSelected = focusedLayerId === chip.dataset.vitalLayer;
      chip.classList.toggle('selected', isSelected);
      chip.closest('.vital-chip')?.classList.toggle('selected', isSelected);
      chip.setAttribute('aria-pressed', String(isSelected));
    }
  });

  /**
   * Toggle a cluster in place (class + aria) rather than re-rendering: the CSS
   * expand/collapse transition needs the existing elements to survive the
   * toggle. The expanded set keeps store-driven re-renders consistent.
   */
  const toggleCluster = (chipEl: HTMLElement): void => {
    const id = chipEl.dataset.vitalCluster!;
    const nowExpanded = !expandedClusters.has(id);
    if (nowExpanded) expandedClusters.add(id);
    else expandedClusters.delete(id);
    chipEl.setAttribute('aria-expanded', String(nowExpanded));
    chipEl.closest('.vital-cluster')?.classList.toggle('vital-cluster--expanded', nowExpanded);
  };

  // Event delegation: one click + one keydown listener on the panel root.
  // Chips are recreated each render, so per-chip handlers would leak; delegating
  // to the stable `el` avoids that entirely.
  const onPanelClick = (e: MouseEvent): void => {
    const visibilityTarget = (e.target as Element).closest('[data-visibility-layer]') as HTMLElement | null;
    if (visibilityTarget) {
      const layerId = visibilityTarget.dataset.visibilityLayer!;
      visibility.toggle(layerId);
      return;
    }
    const clusterTarget = (e.target as Element).closest('[data-vital-cluster]') as HTMLElement | null;
    if (clusterTarget) {
      toggleCluster(clusterTarget);
      return;
    }

    const target = (e.target as Element).closest('[data-vital-layer]') as HTMLElement | null;
    if (!target) {
      const rowFocus = (e.target as Element)
        .closest('.vital-chip.focusable')
        ?.querySelector<HTMLElement>('[data-vital-layer]');
      const rowLayerId = rowFocus?.dataset.vitalLayer;
      if (rowLayerId) {
        if (!visibility.isVisible(rowLayerId)) visibility.toggle(rowLayerId);
        focus.toggle(rowLayerId);
        if (el.classList.contains('vitals--expanded')) setSheetExpanded(false);
        return;
      }
      const informational = (e.target as Element).closest('[data-vital-id]') as HTMLElement | null;
      informational?.focus();
      return;
    }
    const layerId = target.dataset.vitalLayer;
    if (layerId) {
      if (!visibility.isVisible(layerId)) visibility.toggle(layerId);
      focus.toggle(layerId);
      if (el.classList.contains('vitals--expanded')) setSheetExpanded(false);
    }
  };

  const onPanelKeyDown = (e: KeyboardEvent): void => {
    if ((e.target as Element).closest('button[data-visibility-layer], button[data-vital-layer]')) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const clusterTarget = (e.target as Element).closest('[data-vital-cluster]') as HTMLElement | null;
    if (clusterTarget) {
      if (e.key === ' ') e.preventDefault(); // prevent scroll on Space
      toggleCluster(clusterTarget);
      return;
    }

    const target = (e.target as Element).closest('[data-vital-layer]') as HTMLElement | null;
    if (!target) return;
    const layerId = target.dataset.vitalLayer;
    if (!layerId) return;
    if (e.key === ' ') e.preventDefault(); // prevent scroll on Space
    if (!visibility.isVisible(layerId)) visibility.toggle(layerId);
    focus.toggle(layerId);
    if (el.classList.contains('vitals--expanded')) setSheetExpanded(false);
  };
  el.addEventListener('click', onPanelClick);
  el.addEventListener('keydown', onPanelKeyDown);

  // Controls slot — the caller mounts the map controls here (kept as a separate
  // slot so this module stays decoupled from the map/theme/geo deps).
  //
  // Desktop: the slot is a toolbar at the bottom of the panel. Mobile: #vitals
  // has a backdrop-filter, which makes it the containing block for fixed-position
  // descendants — so a `position: fixed` top-right cluster can't escape it. We
  // reparent the slot to <body> on mobile so it floats against the viewport.
  const controlsSlot = document.createElement('div');
  controlsSlot.className = 'vital-controls';
  const controlsMq = window.matchMedia(
    '(max-width: 640px), (max-width: 960px) and (max-height: 500px)',
  );
  const placeControls = (): void => {
    if (controlsMq.matches) document.body.appendChild(controlsSlot);
    else el.appendChild(controlsSlot);
  };
  placeControls();
  controlsMq.addEventListener('change', placeControls);

  // On mobile the sheet body scrolls horizontally; fade only that body so the
  // grab handle and headline summary remain stable.
  const detachEdgeFade = attachEdgeFade(scrollEl);

  let destroyed = false;
  const destroy = (): void => {
    if (destroyed) return;
    destroyed = true;
    clearInterval(ageTimer);
    offStore();
    offVisibility();
    offFocus();
    controlsMq.removeEventListener('change', placeControls);
    sheetHeader.removeEventListener('click', onSheetClick);
    el.removeEventListener('click', onPanelClick);
    el.removeEventListener('keydown', onPanelKeyDown);
    controlsSlot.remove();
    destroyTipLayer();
    detachEdgeFade();
    sheetController.destroy();
    observer.disconnect();
    el.replaceChildren();
  };
  // Cleanup on element removal (best-effort via MutationObserver)
  const observer = new MutationObserver(() => {
    if (typeof document === 'undefined' || !document.contains(el)) {
      destroy();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return { controlsSlot, setSheetExpanded, destroy };
}
